import React, { useEffect, useState } from "react";
import { X, AlertCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CIKIS_SEBEP_LABELS } from "./constants";
import { Field } from "./shared";
import { FieldHistory } from "./FieldHistory";
import { tcError } from "@/lib/tc";
import { PersonnelDocsField, uploadPersonnelDoc } from "./PersonnelDocsField";
import { MealCardFields, EMPTY_CARD, loadPersonCard, saveCard } from "./MealCardFields";
import { DateInput } from "@/components/DateInput";

// Fields accepted by backend PersonnelCreate (model has extra="forbid"). Anything
// else on the editing object (id, created_at, server-side extras) must be stripped
// before PUT/POST to avoid 422 Pydantic errors.
const ALLOWED_FIELDS = [
  "sicil_no", "ad_soyad", "pozisyon", "aylik_brut", "maas_tipi",
  "calisma_sablonu_id", "yemek_gun_auto",
  "ise_baslama_tarihi", "ise_cikis_tarihi", "cikis_sebebi", "aktif", "iban", "yemek_gunluk", "telefon",
];

const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const ayTr = (k) => (k === "2000-01" ? "Başlangıç" : `${k.slice(5)}.${k.slice(0, 4)}`);

function SalaryHistory({ items }) {
  return (
    <details className="mt-1 text-[11px]" data-testid="salary-history">
      <summary className="cursor-pointer text-slate-600 font-semibold">Maaş geçmişi ({items.length})</summary>
      <ul className="mt-1 space-y-0.5">
        {[...items].reverse().map((h) => (
          <li key={h.ay} className="flex flex-wrap gap-x-2 whitespace-nowrap"><span className="w-20 text-slate-500">{ayTr(h.ay)}</span>
            <span className="font-mono-tab">{Number(h.aylik_brut).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</span>
            <span className="uppercase text-slate-400">{h.maas_tipi}</span><span className="text-slate-400 truncate">{h.not || ""}</span></li>
        ))}
      </ul>
    </details>
  );
}

export default function PersonnelModal({ editing, onClose, onSaved }) {
  const { canSeeMoney, canEditMoney, isAdmin } = useAuth();
  const locked = !!editing && !isAdmin;
  const lockCls = locked ? " bg-slate-100 text-slate-500 cursor-not-allowed" : "";
  const [form, setForm] = useState(editing || {
    sicil_no: "",
    ad_soyad: "",
    pozisyon: "",
    aylik_brut: "",
    maas_tipi: "net",
    calisma_sablonu_id: "",
    yemek_gun_auto: true,
    ise_baslama_tarihi: new Date().toISOString().split("T")[0],
    ise_cikis_tarihi: "",
    cikis_sebebi: "",
    aktif: true,
    iban: "",
    yemek_gunluk: "",
    telefon: "",
  });
  const [pendingDocs, setPendingDocs] = useState({});
  const [card, setCard] = useState(EMPTY_CARD);
  const [yearRate, setYearRate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [workTemplates, setWorkTemplates] = useState([]);
  const [posTemplates, setPosTemplates] = useState([]);
  const [showNewPos, setShowNewPos] = useState(false);
  const [newPos, setNewPos] = useState({ isim: "", varsayilan_sablon_id: "", varsayilan_maas: "", varsayilan_maas_tipi: "net" });

  const salaryChanged = !!editing && (Number(form.aylik_brut || 0) !== Number(editing.aylik_brut || 0) || form.maas_tipi !== editing.maas_tipi);
  useEffect(() => {
    api.get("/work-day-templates").then((r) => setWorkTemplates(r.data?.rows || [])).catch(() => {});
    api.get("/position-templates").then((r) => setPosTemplates(r.data?.rows || [])).catch(() => {});
    api.get("/settings/yearly-rates").then((r) => setYearRate(r.data.current)).catch(() => {});
    if (editing?.id) loadPersonCard(editing.id).then((c) => c && setCard({ ...EMPTY_CARD, ...c })).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-pasif on exit date
  useEffect(() => {
    if (form.ise_cikis_tarihi && form.aktif) {
      setForm((f) => ({ ...f, aktif: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.ise_cikis_tarihi]);

  const applyPositionTemplate = (posIsim) => {
    // UI now restricts selection to known templates (<select>); defensive lookup
    // kept so programmatic callers (e.g. right after saveNewPos) still work.
    const pt = posTemplates.find((p) => p.isim === posIsim);
    if (!pt) {
      setForm((f) => ({ ...f, pozisyon: posIsim }));
      return;
    }
    setForm((f) => ({
      ...f,
      pozisyon: pt.isim,
      calisma_sablonu_id: pt.varsayilan_sablon_id || f.calisma_sablonu_id,
      aylik_brut: pt.varsayilan_maas != null ? String(pt.varsayilan_maas) : f.aylik_brut,
      maas_tipi: pt.varsayilan_maas_tipi || f.maas_tipi,
    }));
    toast.success(`Pozisyon şablonu uygulandı: ${pt.isim}`);
  };

  const saveNewPos = async () => {
    if (!newPos.isim?.trim()) return toast.error("Pozisyon adı gerekli");
    try {
      const payload = {
        isim: newPos.isim.trim(),
        varsayilan_sablon_id: newPos.varsayilan_sablon_id || null,
        varsayilan_maas: newPos.varsayilan_maas ? Number(newPos.varsayilan_maas) : null,
        varsayilan_maas_tipi: newPos.varsayilan_maas_tipi || "net",
      };
      const r = await api.post("/position-templates", payload);
      toast.success("Pozisyon şablonu eklendi");
      setPosTemplates((xs) => [...xs, r.data.template]);
      applyPositionTemplate(r.data.template.isim);
      setShowNewPos(false);
      setNewPos({ isim: "", varsayilan_sablon_id: "", varsayilan_maas: "", varsayilan_maas_tipi: "net" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eklenemedi");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ad_soyad || !form.sicil_no) {
      toast.error("Lütfen gerekli alanları doldurun");
      return;
    }
    const tcErr = tcError(form.sicil_no);
    if (tcErr && !window.confirm(`TC Kimlik No geçersiz görünüyor: ${tcErr}.\n\nYine de kaydedilsin mi?`)) return;
    if (isAdmin && !form.aylik_brut && !window.confirm("Maaş girilmedi. Personel \"Maaş bekleniyor\" olarak kaydedilsin mi?")) return;
    setSaving(true);
    try {
      const base = Object.fromEntries(ALLOWED_FIELDS.map((k) => [k, form[k]]));
      const payload = {
        ...base,
        ise_cikis_tarihi: form.ise_cikis_tarihi || null,
        cikis_sebebi: form.ise_cikis_tarihi ? (form.cikis_sebebi || "fesih") : null,
        calisma_sablonu_id: form.calisma_sablonu_id || null,
        yemek_gunluk: form.yemek_gunluk === "" || form.yemek_gunluk == null ? null : Number(form.yemek_gunluk),
      };
      if (isAdmin) {
        payload.aylik_brut = form.aylik_brut === "" || form.aylik_brut == null ? (editing ? 0 : null) : Number(form.aylik_brut);
        if (editing && salaryChanged) payload.maas_gecerlilik_ay = form.maas_gecerlilik_ay || thisMonth();
      } else if (editing) {
        delete payload.aylik_brut;
      } else {
        payload.aylik_brut = null;
      }
      let saved;
      if (editing) {
        saved = (await api.put(`/personnel/${editing.id}`, payload)).data;
        toast.success("Personel güncellendi");
      } else {
        try {
          saved = (await api.post("/personnel", payload)).data;
        } catch (err) {
          const d = err?.response?.data?.detail;
          if (err?.response?.status !== 409 || d?.code !== "duplicate_personnel") throw err;
          const list = (d.existing || []).map((p) => `• ${p.ad_soyad} · TC ${p.sicil_no}${p.pozisyon ? ` · ${p.pozisyon}` : ""}${p.aktif === false ? " (pasif)" : ""}`).join("\n");
          if (!window.confirm(`⚠️ MÜKERRER KAYIT\n\n${d.message}\n\n${list}\n\nYine de yeni personel olarak eklemek istiyor musunuz?`)) {
            toast.info("Ekleme iptal edildi");
            return;
          }
          saved = (await api.post("/personnel?force=true", payload)).data;
        }
        toast.success(isAdmin ? "Personel eklendi" : "Personel eklendi · maaş için Admin'e bildirim gönderildi");
        for (const [kind, file] of Object.entries(pendingDocs)) {
          try { await uploadPersonnelDoc(saved.id, kind, file); } catch { toast.error("Bildirge yüklenemedi · düzenle ekranından tekrar deneyin"); }
        }
      }
      if (String(card.kart_no || "").trim()) {
        try { await saveCard(card, saved); } catch (err) { toast.error(extractErrorMessage(err, "Yemek kartı kaydedilemedi")); }
      }
      onSaved();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Kaydedilemedi"));
    } finally {
      setSaving(false);
    }
  };

  const maasLabel = form.maas_tipi === "net" ? "Sabit (Net) Maaş (₺)" : "Sabit (Brüt) Maaş (₺)";

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-xl max-h-[90vh] overflow-y-auto" data-testid="personnel-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display text-lg font-bold">{editing ? "Personel Düzenle" : "Yeni Personel"}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="TC Kimlik No *">
              <input data-testid="input-sicil" required inputMode="numeric" maxLength={20} value={form.sicil_no} onChange={(e) => setForm({ ...form, sicil_no: e.target.value.trim() })}
                className={`input font-mono-tab ${form.sicil_no && tcError(form.sicil_no) ? "border-amber-400 bg-amber-50" : ""}`} />
              {form.sicil_no && (tcError(form.sicil_no)
                ? <div className="text-[11px] text-amber-700 mt-0.5" data-testid="tc-warning">Uyarı: {tcError(form.sicil_no)}</div>
                : <div className="text-[11px] text-emerald-700 mt-0.5" data-testid="tc-ok">Geçerli TC Kimlik No</div>)}
            </Field>
            <Field label="Ad Soyad *">
              <input data-testid="input-adsoyad" required value={form.ad_soyad} onChange={(e) => setForm({ ...form, ad_soyad: e.target.value })} className="input" />
            </Field>
          </div>

          <Field label="Pozisyon * (yalnızca şablondan seçilebilir)">
            <div className="flex gap-1.5">
              <select
                data-testid="input-pozisyon"
                required
                value={form.pozisyon}
                onChange={(e) => applyPositionTemplate(e.target.value)}
                className="input flex-1"
              >
                <option value="" disabled>— Şablon seçin —</option>
                {posTemplates.map((p) => <option key={p.id} value={p.isim}>{p.isim}</option>)}
                {/* Legacy value preservation: when editing an existing personnel whose
                    `pozisyon` no longer matches any template (e.g. template was renamed
                    or deleted), expose it as a tagged option so edits don't silently
                    reset the field. Backend will still reject save unless the user
                    picks a currently-valid template. */}
                {form.pozisyon &&
                 !posTemplates.some((p) => p.isim.toLowerCase() === form.pozisyon.toLowerCase()) && (
                  <option value={form.pozisyon}>{form.pozisyon} (mevcut — şablonda yok)</option>
                )}
              </select>
              <button
                type="button"
                data-testid="add-position-btn"
                onClick={() => setShowNewPos(true)}
                className="px-2 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-md text-xs font-semibold inline-flex items-center gap-1 shrink-0"
                title="Yeni pozisyon şablonu ekle"
              >
                <Plus size={13} /> Yeni Şablon
              </button>
            </div>
            {posTemplates.length === 0 && (
              <div className="mt-1 text-[11px] text-rose-600">
                Henüz pozisyon şablonu yok. Önce "Yeni Şablon" ile ekleyin.
              </div>
            )}
            {form.pozisyon &&
             !posTemplates.some((p) => p.isim.toLowerCase() === form.pozisyon.toLowerCase()) && (
              <div className="mt-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                Bu personelin mevcut pozisyonu artık şablonlarda yok. Kaydetmeden önce listeden geçerli bir pozisyon seçin veya yeni şablon ekleyin.
              </div>
            )}
          </Field>

          {showNewPos && (
            <div className="bg-slate-50 border border-slate-300 rounded-lg p-3 space-y-2" data-testid="new-position-form">
              <div className="text-xs font-bold text-slate-700">Yeni Pozisyon Şablonu</div>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="new-pos-name" placeholder="İsim" value={newPos.isim} onChange={(e) => setNewPos({ ...newPos, isim: e.target.value })} className="input" />
                <select data-testid="new-pos-sablon" value={newPos.varsayilan_sablon_id} onChange={(e) => setNewPos({ ...newPos, varsayilan_sablon_id: e.target.value })} className="input">
                  <option value="">Çalışma şablonu (opsiyonel)</option>
                  {workTemplates.map((w) => <option key={w.id} value={w.id}>{w.isim}</option>)}
                </select>
                <input data-testid="new-pos-maas" type="number" placeholder="Varsayılan maaş (₺)" value={newPos.varsayilan_maas} onChange={(e) => setNewPos({ ...newPos, varsayilan_maas: e.target.value })} className="input" />
                <select data-testid="new-pos-tipi" value={newPos.varsayilan_maas_tipi} onChange={(e) => setNewPos({ ...newPos, varsayilan_maas_tipi: e.target.value })} className="input">
                  <option value="net">Net Maaş</option>
                  <option value="brut">Brüt Maaş</option>
                </select>
              </div>
              <div className="flex justify-end gap-1">
                <button type="button" onClick={() => setShowNewPos(false)} className="px-2 py-1 text-xs text-slate-600 font-semibold">İptal</button>
                <button type="button" data-testid="save-new-pos" onClick={saveNewPos} className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs font-semibold">Kaydet & Uygula</button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <Field label="Maaş Tipi">
              <select data-testid="input-maas-tipi" disabled={locked} value={form.maas_tipi} onChange={(e) => setForm({ ...form, maas_tipi: e.target.value })} className={`input font-semibold${lockCls}`}>
                <option value="net">Net (üstüne vergi eklenir)</option>
                <option value="brut">Brüt (kesinti düşülür)</option>
              </select>
            </Field>
            <Field label={maasLabel}>
              {!isAdmin && !editing ? (
                <input disabled value="Admin girecek" data-testid="input-brut-pending" className="input text-xs bg-orange-50 text-orange-700 cursor-not-allowed" />
              ) : canEditMoney ? (
                <>
                <input data-testid="input-brut" type="number" step="0.01" disabled={locked} placeholder="Maaş bekleniyor" title={locked ? "Yalnızca Admin değiştirebilir" : ""} value={form.aylik_brut} onChange={(e) => setForm({ ...form, aylik_brut: e.target.value })} className={`input font-mono-tab${lockCls}`} />
                {editing && isAdmin && salaryChanged && (
                  <label className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                    Geçerli olacağı ay
                    <input type="month" value={form.maas_gecerlilik_ay || thisMonth()} onChange={(e) => setForm({ ...form, maas_gecerlilik_ay: e.target.value })}
                      className="border border-emerald-300 rounded px-1 py-0.5 bg-white" data-testid="input-maas-gecerlilik" />
                    <span className="text-slate-500 w-full">Önceki aylar eski maaşla kalır. İleri bir ay seçerseniz o aya kadar mevcut maaş geçerli olur.</span>
                    {(() => {
                      const from = form.maas_gecerlilik_ay || thisMonth();
                      const later = (editing.maas_gecmisi || []).filter((h) => h.ay >= from && h.ay !== "2000-01");
                      return later.length > 0 && (
                        <span className="text-amber-700 w-full" data-testid="maas-later-entries-note">
                          Bu aydan sonraki {later.length} maaş kaydı ({later.map((h) => h.ay.split("-").reverse().join(".")).join(", ")}) yeni maaşla değiştirilecek.
                        </span>
                      );
                    })()}
                  </label>
                )}
                {editing && isAdmin && (editing.maas_gecmisi || []).length > 1 && <SalaryHistory items={editing.maas_gecmisi} />}
                </>
              ) : (
                <input disabled value="•••••" className="input font-mono-tab bg-slate-100 text-slate-500 cursor-not-allowed" />
              )}
            </Field>
            <Field label="İşe Başlama Tarihi">
              <DateInput data-testid="input-tarih" value={form.ise_baslama_tarihi} onChange={(e) => setForm({ ...form, ise_baslama_tarihi: e.target.value })} className="input" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Telefon Numarası">
              <input data-testid="input-telefon" type="tel" inputMode="tel" placeholder="05xx xxx xx xx" value={form.telefon || ""}
                onChange={(e) => setForm({ ...form, telefon: e.target.value })} className="input font-mono-tab" />
            </Field>
            <div />
          </div>
          <Field label={`IBAN${locked ? " (yalnızca Admin değiştirebilir)" : ""}`}>
            <input data-testid="input-iban" disabled={locked} placeholder="TR00 0000 0000 0000 0000 0000 00" value={form.iban || ""}
              onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })} className={`input font-mono-tab${lockCls}`} />
          </Field>
          <Field label={`Günlük Yemek Ücreti (₺)${locked ? " · yalnızca Admin değiştirir" : ""}`}>
            <input data-testid="input-yemek-gunluk" type="number" min="0" step="0.01" disabled={locked}
              value={form.yemek_gunluk ?? ""} onChange={(e) => setForm({ ...form, yemek_gunluk: e.target.value })}
              placeholder={(() => { const pt = posTemplates.find((p) => p.isim === form.pozisyon); return pt?.yemek_yok ? "Pozisyonda yemek ücreti yok" : pt?.varsayilan_yemek != null ? `Pozisyon varsayılanı: ${pt.varsayilan_yemek} ₺` : `Yıl varsayılanı: ${yearRate?.yemek_gunluk ?? "—"} ₺`; })()}
              className={`input font-mono-tab${lockCls}`} />
            <div className="text-[11px] text-slate-500 mt-0.5">Boş bırakılırsa pozisyon, o da yoksa yıl varsayılanı kullanılır · her ay puantajdaki çalışılan günlerle çarpılır.{yearRate ? ` Yol: ${yearRate.yol_aylik} ₺/ay (${yearRate.yol_gunluk} ₺/gün) otomatik.` : ""}</div>
          </Field>
          <MealCardFields card={card} setCard={setCard} adSoyad={form.ad_soyad} telefon={form.telefon} />
          <div>
            <div className="text-xs font-bold text-slate-700 mb-1">SGK Bildirgeleri</div>
            <PersonnelDocsField personnelId={editing?.id} pending={pendingDocs} setPending={setPendingDocs} />
          </div>
          {locked && <div className="text-[11px] text-slate-500 -mt-2" data-testid="salary-lock-note">Maaş, maaş tipi, IBAN ve günlük yemek kilitli · yalnızca Admin değiştirebilir.</div>}
          {editing && canSeeMoney && <FieldHistory personnelId={editing.id} />}

          <div className="grid grid-cols-2 gap-4">
            <Field label="Çalışma Günleri Şablonu">
              <select
                data-testid="input-calisma-sablon"
                value={form.calisma_sablonu_id || ""}
                onChange={(e) => setForm({ ...form, calisma_sablonu_id: e.target.value })}
                className="input font-semibold"
              >
                <option value="">(Pozisyon şablonundan al / Pzt-Cum)</option>
                {workTemplates.map((w) => <option key={w.id} value={w.id}>{w.isim}</option>)}
              </select>
            </Field>
            <Field label="Yemek Gün Sayısı">
              <label className="flex items-center gap-2 text-sm pt-2">
                <input
                  type="checkbox"
                  data-testid="input-yemek-auto"
                  checked={!!form.yemek_gun_auto}
                  onChange={(e) => setForm({ ...form, yemek_gun_auto: e.target.checked })}
                />
                Otomatik — çalışılan güne eşitle
              </label>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="İşten Çıkış Tarihi">
              <DateInput
                data-testid="input-cikis"
               
                value={form.ise_cikis_tarihi || ""}
                onChange={(e) => setForm({ ...form, ise_cikis_tarihi: e.target.value })}
                className="input"
              />
            </Field>
            {form.ise_cikis_tarihi ? (
              <Field label="Çıkış Sebebi">
                <select
                  data-testid="input-sebep"
                  value={form.cikis_sebebi || "fesih"}
                  onChange={(e) => setForm({ ...form, cikis_sebebi: e.target.value })}
                  className="input font-semibold"
                >
                  {Object.entries(CIKIS_SEBEP_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
            ) : (
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm pb-2">
                  <input
                    data-testid="input-aktif"
                    type="checkbox"
                    checked={form.aktif}
                    onChange={(e) => setForm({ ...form, aktif: e.target.checked })}
                  />
                  Aktif personel
                </label>
              </div>
            )}
          </div>

          {form.ise_cikis_tarihi && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-900 flex items-center gap-2">
              <AlertCircle size={14} /> İşten çıkış tarihi girildi — kayıt <strong>pasif</strong> olarak işaretlenecek.
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50">İptal</button>
            <button data-testid="save-personnel-button" type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
      <style>{`.input { width: 100%; padding: 8px 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .input:focus { border-color: #0F766E; box-shadow: 0 0 0 3px rgba(15,118,110,0.15); }`}</style>
    </div>
  );
}
